import { Analytics } from '@analytics';
import { Button, Chip, TextField } from '@material-ui/core';
import { capitalize } from '@material-ui/core/utils';
import { makeStyles } from '@material-ui/styles';
import { lensPath, lensProp, set } from 'ramda';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactReduxContext } from 'react-redux';
import { ReviewSite } from '../../../components/TapToTip/Setup/ToursSection/constants';
import { scanTourFromUrl } from '../../../api/taptotip/tour';
import { EXPERIMENTAL_IMPORT_TOUR_FROM_URL } from '../../../utils/experimentalFeatures';
import {
  isQuestionnairesEnabledForCompany,
  languageCodesToLanguageNames,
  supportedLanguages
} from '../../../utils/helpers';
import CustomizableModal from '../../Common/CustomizableModal';
import messages from './messages';

// Card Un67ehPJ — the modal only surfaces title + description, so fold the
// richer extracted fields (highlights, inclusions, meeting point, duration)
// into the description as tidy sections. The operator reviews and trims before
// saving; nothing is auto-saved.
export function composeTourDescription(fields = {}) {
  const parts = [];
  if (fields.longDescription) parts.push(fields.longDescription.trim());
  if (fields.highlights && fields.highlights.length) {
    parts.push(`Highlights:\n${fields.highlights.map(h => `• ${h}`).join('\n')}`);
  }
  if (fields.inclusions && fields.inclusions.length) {
    parts.push(`What's included:\n${fields.inclusions.map(i => `• ${i}`).join('\n')}`);
  }
  if (fields.meetingPointDescription) {
    parts.push(`Meeting point: ${fields.meetingPointDescription.trim()}`);
  }
  if (fields.durationText) parts.push(`Duration: ${fields.durationText.trim()}`);
  return parts.join('\n\n');
}

const REVIEW_SITE_KEYS = [
  ReviewSite.TripAdvisor,
  ReviewSite.Google,
  ReviewSite.Yelp,
  ReviewSite.TrustPilot,
  ReviewSite.Feefo
];

/**
 * Any TripAdvisor address for the tour will do: the backend builds the review
 * link from its `-d<number>` tour ID. This only catches a TripAdvisor address
 * with no tour in it (a city page, an app share link) while the operator can
 * still fix it, because the modal closes on save and a server error would be
 * lost. Mirrors toTripAdvisorReviewUrl in backend/src/domain/tour/bl.
 */
export function isTripAdvisorLinkWithoutTour(raw) {
  const trimmed = `${raw || ''}`.trim();
  if (!trimmed) return false;
  let url;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch (e) {
    return false;
  }
  if (!url.hostname.toLowerCase().split('.').includes('tripadvisor')) return false;
  return !/-d\d+(?=[-.]|$)/.test(url.pathname);
}

/**
 * Whether the modal's confirm button is live.
 *
 * Card 324 — a tour normally needs at least one review link, because the tour
 * exists to send guests to a review site. A company using guest questionnaires
 * sets tours up to attach a feedback form to, and those tours may have no
 * review presence at all (RIT's multi-day coach tours), so for them the links
 * are optional. The backend never required them.
 */
export function canConfirmTour(state, { reviewLinksOptional = false } = {}) {
  const hasReviewLink = REVIEW_SITE_KEYS.some(key => state[key] && state[key].url);
  return (
    state.title.length > 3 &&
    state.desc.length > 3 &&
    (reviewLinksOptional || hasReviewLink) &&
    !isTripAdvisorLinkWithoutTour(state[ReviewSite.TripAdvisor] && state[ReviewSite.TripAdvisor].url)
  );
}

// The signed-in user, when there is a store. The modal is also rendered bare in
// tests, where useSelector would throw for want of a Provider.
const useOptionalUser = () => {
  const context = useContext(ReactReduxContext);
  return context && context.store ? context.store.getState().user?.user : undefined;
};

const useStyles = makeStyles(() => ({
  input: {
    width: '100%',
    margin: '20px 0 0 0',
    '& .MuiInputBase-root': {
      flexWrap: 'wrap'
    }
  }
}));

// Each modal instance gets its own id prefix so its fields can be tied to their
// labels (htmlFor/id) without clashing with another instance on the page.
let modalInstanceCount = 0;
const useFieldIdPrefix = () => {
  const [prefix] = useState(() => {
    modalInstanceCount += 1;
    return `tour-modal-${modalInstanceCount}`;
  });
  return prefix;
};

const emptyState = {
  primaryLanguage: 'en',
  title: '',
  desc: '',
  [ReviewSite.Google]: {
    name: ReviewSite.Google,
    url: ''
  },
  [ReviewSite.TripAdvisor]: {
    name: ReviewSite.TripAdvisor,
    url: ''
  },
  [ReviewSite.Yelp]: {
    name: ReviewSite.Yelp,
    url: ''
  },
  [ReviewSite.TrustPilot]: {
    name: ReviewSite.TrustPilot,
    url: ''
  },
  [ReviewSite.Feefo]: {
    name: ReviewSite.Feefo,
    url: ''
  }
};

function getInitialState(tourDetails) {
  if (tourDetails) {
    return {
      primaryLanguage: tourDetails.primaryLanguage,
      title: tourDetails.title,
      desc: tourDetails.description,
      [ReviewSite.Google]: {
        name: ReviewSite.Google,
        url: tourDetails?.reviewSites?.find(
          i => i.name.toLowerCase() == ReviewSite.Google.toLowerCase()
        )?.url
      },
      [ReviewSite.TripAdvisor]: {
        name: ReviewSite.TripAdvisor,
        url: tourDetails?.reviewSites?.find(
          i => i.name.toLowerCase() == ReviewSite.TripAdvisor.toLowerCase()
        )?.url
      },
      [ReviewSite.Yelp]: {
        name: ReviewSite.Yelp,
        url: tourDetails?.reviewSites?.find(
          i => i.name.toLowerCase() == ReviewSite.Yelp.toLowerCase()
        )?.url
      },
      [ReviewSite.TrustPilot]: {
        name: ReviewSite.TrustPilot,
        url: tourDetails?.reviewSites?.find(
          i => i.name.toLowerCase() == ReviewSite.TrustPilot.toLowerCase()
        )?.url
      },
      [ReviewSite.Feefo]: {
        name: ReviewSite.Feefo,
        url: tourDetails?.reviewSites?.find(
          i => i.name.toLowerCase() == ReviewSite.Feefo.toLowerCase()
        )?.url
      }
    };
  }
  return emptyState;
}

const CreateTourModal = ({
  isOpen,
  setIsOpen,
  addTapToTipTour,
  updateTapToTipTour,
  tourDetails,
  setIsManageGuidesOpen
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const reviewLinksOptional = isQuestionnairesEnabledForCompany(useOptionalUser());
  // Field ids: MUI only links a TextField's visible label to its input (and so
  // gives it an accessible name) when the TextField has an id.
  const idPrefix = useFieldIdPrefix();
  const fieldId = name => `${idPrefix}-${name}`;

  const isEditMode = updateTapToTipTour && tourDetails;

  const [state, setState] = useState(getInitialState(tourDetails));
  const tripAdvisorLinkWithoutTour = isTripAdvisorLinkWithoutTour(
    state[ReviewSite.TripAdvisor] && state[ReviewSite.TripAdvisor].url
  );
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importNote, setImportNote] = useState('');
  // Card 265 — the page answered but reads like a "page not found" screen.
  // Amber, not green: the fields are filled (it's a heuristic) but this must
  // not read as a successful import.
  const [importWarning, setImportWarning] = useState('');

  useEffect(() => {
    if (isOpen) {
      setState(getInitialState(tourDetails));
      setImportUrl('');
      setImportError('');
      setImportNote('');
      setImportWarning('');
    }
  }, [tourDetails, isOpen]);

  async function handleImportFromUrl() {
    if (!importUrl.trim() || isImporting) return;
    setIsImporting(true);
    setImportError('');
    setImportNote('');
    setImportWarning('');
    try {
      const { fields, source, warning } = await scanTourFromUrl(
        importUrl.trim()
      );
      const desc = composeTourDescription(fields);
      if (!fields.title && !desc) {
        setImportError(intl.formatMessage(messages.importEmpty));
      } else {
        setState(prev => ({
          ...prev,
          title: fields.title || prev.title,
          desc: desc || prev.desc
        }));
        if (warning) {
          setImportWarning(
            warning.message || intl.formatMessage(messages.importSoftNotFound)
          );
        } else {
          setImportNote(
            source === 'reader'
              ? intl.formatMessage(messages.importedViaReader)
              : intl.formatMessage(messages.imported)
          );
        }
        Analytics.track('TTT tour imported from URL', {
          warning: warning ? warning.code : undefined
        });
      }
    } catch (err) {
      // Card QyIKo5Eb — the backend refuses TripAdvisor/Viator links (and other
      // bad addresses) with an operator-facing DomainError. Those messages say
      // exactly what to do about it, so show them instead of the generic
      // "couldn't read that page"; anything else (network, 5xx) falls back.
      const serverMessage = err && err.status === 400 ? err.message : '';
      setImportError(serverMessage || intl.formatMessage(messages.importFailed));
    } finally {
      setIsImporting(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
    setState(getInitialState());
  }

  function handleChange(lens) {
    return event => {
      const newState = set(lens, event.target.value, state);
      setState(newState);
    };
  }

  const getModalBody = () => {
    return (
      <div className="w-[80vw] max-w-[470px]">
        <h3 className="text-xl font-bold text-slate-800 mt-5 mb-0">
          {isEditMode
            ? intl.formatMessage(messages.updateTour)
            : intl.formatMessage(messages.createTour)}
        </h3>

        {EXPERIMENTAL_IMPORT_TOUR_FROM_URL && (
          <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
            <p id={fieldId('import-title')} className="text-sm font-semibold text-slate-700 m-0">
              {intl.formatMessage(messages.importTitle)}
            </p>
            <p className="text-xs text-slate-500 mt-1 mb-2">
              {intl.formatMessage(messages.importSubtitle)}
            </p>
            <div className="flex gap-2 items-stretch">
              <TextField
                size="small"
                className="flex-1"
                id={fieldId('import-url')}
                inputProps={{ 'aria-labelledby': fieldId('import-title') }}
                placeholder="https://yourwebsite.com/your-tour"
                variant="outlined"
                value={importUrl}
                disabled={isImporting}
                onChange={e => setImportUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleImportFromUrl()}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                onClick={handleImportFromUrl}
                disabled={isImporting || !importUrl.trim()}
                variant="contained"
                color="primary"
                className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-lg !shadow-sm !normal-case whitespace-nowrap"
              >
                {isImporting
                  ? intl.formatMessage(messages.importing)
                  : intl.formatMessage(messages.importButton)}
              </Button>
            </div>
            {importError && (
              <p className="text-xs text-red-600 mt-2 mb-0">{importError}</p>
            )}
            {importWarning && (
              <p
                role="alert"
                className="text-xs text-amber-700 mt-2 mb-0"
              >
                {importWarning}
              </p>
            )}
            {importNote && (
              <p className="text-xs text-emerald-600 mt-2 mb-0">{importNote}</p>
            )}
          </div>
        )}

        <TextField
          className={classes.input}
          id={fieldId('title')}
          label={intl.formatMessage(messages.tourName)}
          variant="outlined"
          value={state.title}
          onChange={handleChange(lensProp('title'))}
          InputLabelProps={{
            shrink: true
          }}
        />
        <TextField
          multiline
          className={classes.input}
          id={fieldId('description')}
          label={intl.formatMessage(messages.longDesc)}
          variant="outlined"
          value={state.desc}
          rows={5}
          onChange={handleChange(lensProp('desc'))}
          InputLabelProps={{
            shrink: true
          }}
        />
        <div className="mt-2.5">
          <TextField
            className={classes.input}
            id={fieldId('google')}
            label={intl.formatMessage(messages.googleReviewURL)}
            variant="outlined"
            value={state[ReviewSite.Google].url}
            onChange={handleChange(lensPath([ReviewSite.Google, 'url']))}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
        <div className="mt-2.5">
          <TextField
            className={classes.input}
            id={fieldId('tripadvisor')}
            label={intl.formatMessage(messages.tripAdvisorReviewURL)}
            variant="outlined"
            value={state[ReviewSite.TripAdvisor].url}
            onChange={handleChange(lensPath([ReviewSite.TripAdvisor, 'url']))}
            error={tripAdvisorLinkWithoutTour}
            helperText={intl.formatMessage(
              tripAdvisorLinkWithoutTour
                ? messages.tripAdvisorLinkWithoutTour
                : messages.tripAdvisorAnyLink
            )}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
        <div className="mt-2.5">
          <TextField
            className={classes.input}
            id={fieldId('yelp')}
            label={intl.formatMessage(messages.yelpReviewURL)}
            variant="outlined"
            value={state[ReviewSite.Yelp].url}
            onChange={handleChange(lensPath([ReviewSite.Yelp, 'url']))}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
        <div className="mt-2.5">
          <TextField
            className={classes.input}
            id={fieldId('trustpilot')}
            label={'TrustPilot Review Url'}
            variant="outlined"
            value={state[ReviewSite.TrustPilot].url}
            onChange={handleChange(lensPath([ReviewSite.TrustPilot, 'url']))}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
        <div className="mt-2.5">
          <TextField
            className={classes.input}
            id={fieldId('feefo')}
            label={'Feefo Review Url'}
            variant="outlined"
            value={state[ReviewSite.Feefo].url}
            onChange={handleChange(lensPath([ReviewSite.Feefo, 'url']))}
            InputLabelProps={{
              shrink: true
            }}
          />
        </div>
        {!isEditMode && (
          <>
            <h3 className="text-xl font-bold text-slate-800 mt-5 mb-0">{intl.formatMessage(messages.inputLanguage)}</h3>
            <h4 className="text-slate-500 text-base font-normal my-4">
              {intl.formatMessage(messages.inputLanguageDesc)}
            </h4>

            <div>
              {supportedLanguages.map(lang => {
                const isActive = lang === state.primaryLanguage;
                return (
                  <Chip
                    data-active={isActive}
                    aria-pressed={isActive}
                    className={`!rounded-md !font-semibold !m-1 ${
                      isActive
                        ? '!border-indigo-500 !text-indigo-600 !shadow-sm'
                        : '!border-slate-300 !text-slate-400'
                    }`}
                    key={lang}
                    variant="outlined"
                    onClick={() =>
                      handleChange(lensProp('primaryLanguage'))({ target: { value: lang } })
                    }
                    color={isActive ? 'primary' : 'default'}
                    label={capitalize(languageCodesToLanguageNames[lang])}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  function handleConfirm() {
    const payload = {
      displayName: state.title,
      description: state.desc,
      primaryLanguage: state.primaryLanguage,
      reviewSites: [
        {
          name: state[ReviewSite.TripAdvisor].name,
          url: state[ReviewSite.TripAdvisor].url
        },
        {
          name: state[ReviewSite.Google].name,
          url: state[ReviewSite.Google].url
        },
        {
          name: state[ReviewSite.Yelp].name,
          url: state[ReviewSite.Yelp].url
        },
        {
          name: state[ReviewSite.TrustPilot].name,
          url: state[ReviewSite.TrustPilot].url
        },
        {
          name: state[ReviewSite.Feefo].name,
          url: state[ReviewSite.Feefo].url
        }
      ]
    };

    if (isEditMode) {
      updateTapToTipTour({
        id: tourDetails._id,
        ...payload
      });
    } else {
      addTapToTipTour(payload);
    }

    Analytics.trackUseFeature('Tours');
    Analytics.track(`TTT tour ${isEditMode ? 'edited' : 'created'}`);
    handleClose();
  }

  const handleManageGuidesClicked = () => {
    handleClose();
    Analytics.track('Manage Guides button clicked');
    setIsManageGuidesOpen(true);
  };

  const getCustomButtons = () => {
    const manageGuidesButton = (
      <Button
        onClick={handleManageGuidesClicked}
        variant="contained"
        color="primary"
        className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !shadow-sm !normal-case"
      >
        {intl.formatMessage(messages.manageGuides)}
      </Button>
    );
    return [manageGuidesButton];
  };

  const isConfirmButtonEnabled = canConfirmTour(state, { reviewLinksOptional });

  return (
    <CustomizableModal
      modern
      isOpen={isOpen}
      onClose={handleClose}
      withHeader={false}
      body={getModalBody()}
      handleFooterCloseButton={handleClose}
      customButtons={isEditMode ? getCustomButtons() : []}
      primaryActionButtonData={{
        isDisabled: !isConfirmButtonEnabled,
        onClick: handleConfirm,
        content: isEditMode
          ? intl.formatMessage(messages.updateTour)
          : intl.formatMessage(messages.createTour)
      }}
    />
  );
};

export default CreateTourModal;
